import React, { useState } from 'react';
import { X } from 'lucide-react';
import { ThemeConfig, CalendarEvent } from '../types';
import { firebaseService } from '../FirebaseService';

interface CalendarViewProps {
  theme: ThemeConfig;
  events: CalendarEvent[];
  setEvents: React.Dispatch<React.SetStateAction<CalendarEvent[]>>;
}

const CalendarView: React.FC<CalendarViewProps> = ({ theme, events, setEvents }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState<Date | null>(null);
  const [newEventTitle, setNewEventTitle] = useState('');

  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();
  const today = new Date();
  
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  
  const days = [];
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(null);
  }
  for (let i = 1; i <= daysInMonth; i++) {
    days.push(i);
  }

  const getEventsForDay = (day: number) => {
    return events.filter(event => 
      event.date.getDate() === day &&
      event.date.getMonth() === currentMonth &&
      event.date.getFullYear() === currentYear
    );
  };

  const handleAddEvent = async () => {
    if (newEventTitle.trim() && showAddModal) {
      const newEvent: CalendarEvent = {
        id: new Date().toISOString(),
        title: newEventTitle,
        date: showAddModal,
        color: ['pink', 'brown', 'green', 'purple', 'blue'][Math.floor(Math.random() * 5)]
      };
      await firebaseService.saveEvent(newEvent);
      setEvents([...events, newEvent]);
      setNewEventTitle('');
      setShowAddModal(null);
    }
  };

  const handleDeleteEvent = async (id: string) => {
    await firebaseService.deleteEvent(id);
    setEvents(events.filter(e => e.id !== id));
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1));
  };

  return (
    <div>
      <h2 className={`text-2xl font-bold ${theme.text} mb-6`}>
        Your Calendar 📅
      </h2>

      <div className={`${theme.cardBg} backdrop-blur-sm rounded-2xl p-6 shadow-lg ${theme.shadow} border ${theme.border}`}>
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={previousMonth}
            className={`${theme.button} text-white px-4 py-2 rounded-lg transition-all`}
          >
            ← Prev
          </button>
          <h3 className={`text-xl md:text-2xl font-bold ${theme.text}`}>
            {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </h3>
          <button
            onClick={nextMonth}
            className={`${theme.button} text-white px-4 py-2 rounded-lg transition-all`}
          >
            Next →
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day) => (
            <div key={day} className={`text-center font-bold ${theme.text} py-2`}>
              <span className="hidden md:inline">{day}</span>
              <span className="md:hidden">{day.slice(0, 3)}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {days.map((day, i) => {
            const dayEvents = day ? getEventsForDay(day) : [];
            const isToday = day === today.getDate() && 
                           currentMonth === today.getMonth() && 
                           currentYear === today.getFullYear();
            
            return (
              <div
                key={i}
                className={`min-h-[60px] md:min-h-[100px] p-2 rounded-lg border ${theme.border} ${
                  day ? `${theme.secondary} hover:${theme.primary} cursor-pointer` : ''
                } ${isToday ? `ring-2 ${theme.accent.replace('bg-', 'ring-')}` : ''} transition-all relative group`}
                onClick={() => day && setShowAddModal(new Date(currentYear, currentMonth, day))}
              >
                {day && (
                  <>
                    <div className={`font-semibold ${isToday ? theme.accent.replace('bg-', 'text-') : theme.text} mb-1`}>
                      {day}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.map(event => (
                        <div
                          key={event.id}
                          className={`text-xs ${theme.accent} text-white px-1 py-0.5 rounded truncate relative pr-5`}
                          title={event.title}
                        >
                          {event.title}
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}
                            className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`${theme.cardBg} backdrop-blur-sm rounded-2xl p-6 max-w-md w-full shadow-2xl`}>
            <h3 className={`text-xl font-bold ${theme.text} mb-4`}>Add Event on {showAddModal.toLocaleDateString()}</h3>
            <input
              type="text"
              value={newEventTitle}
              onChange={(e) => setNewEventTitle(e.target.value)}
              placeholder="Enter event title..."
              className={`w-full p-3 border ${theme.border} rounded-lg mb-4 ${theme.text}`}
              autoFocus
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddModal(null)}
                className={`flex-1 py-2 px-4 rounded-lg border ${theme.border} ${theme.text} hover:${theme.secondary} transition-all`}
              >
                Cancel
              </button>
              <button
                onClick={handleAddEvent}
                className={`flex-1 ${theme.button} text-white py-2 px-4 rounded-lg transition-all`}
              >
                Add Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarView;